
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SparkleIcon } from '../constants.tsx';
import { useMarketStatus } from './useMarketStatus.ts';
import HeroVideoPlayer from './HeroVideoPlayer.tsx';
import HeroAmbience from './HeroAmbience.tsx';

const Hero: React.FC = () => {
  const { t } = useTranslation();
  const { isOpen: isMarketOpen, status, holidayReason } = useMarketStatus();

  return (
    <section id="home" className="hero-bg relative overflow-hidden z-10 pt-24 md:pt-28 xl:pt-32 pb-14">
      <HeroAmbience />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8 relative z-10">

        {/* Main Hero Content - Split Layout: Content & Market Card (Right) | Video Player (Left) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">

          {/* Right Column: Title, Description, CTA Buttons & Compact Market Card */}
          <div className="lg:col-span-5 xl:col-span-5 text-center lg:text-right flex flex-col justify-center">
            
            {/* Sparkle Badge */}
            <div className="flex justify-center lg:justify-start">
              <a href="/#" className="hero-badge hover:opacity-80 relative mb-3 font-medium text-xs sm:text-sm inline-flex items-center gap-2 py-1.5 px-4 rounded-full">
                <SparkleIcon />
                <span>{t('heroSubtitle')}</span>
              </a>
            </div>

            {/* Main Headline */}
            <h1 className="text-white mb-4 text-3xl sm:text-4xl xl:text-5xl font-extrabold leading-tight">
              {t('heroTitle')}
            </h1>

            {/* Description */}
            <p className="max-w-xl mx-auto lg:mx-0 mb-6 font-medium text-sm sm:text-base text-white/70 leading-relaxed">
              {t('heroDescription')}
            </p>

            {/* Action Buttons: Web Dashboard + Desktop + Mobile */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2.5 sm:gap-3 mb-6">
              <a href="https://dash.tad-ai.com" target="_blank" rel="noopener noreferrer" className="hero-button inline-flex items-center justify-center rounded-xl py-2.5 px-5 sm:px-6 text-white text-xs sm:text-sm font-bold ease-in duration-300 shadow-lg shadow-purple-500/20 hover:scale-105">
                {t('heroButton')}
              </a>
              <a
                href="https://www.tad-ai.com/TadAI-Setup.exe"
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-2 rounded-xl py-2.5 px-4 sm:px-5 text-white text-xs sm:text-sm font-bold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-blue-500/20 transition-all hover:scale-105"
              >
                <span>💻</span>
                <span>تحميل الويندوز</span>
              </a>
              <a
                href="https://www.tad-ai.com/TADAI-Instaler.apk"
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-2 rounded-xl py-2.5 px-4 sm:px-5 text-white text-xs sm:text-sm font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/20 transition-all hover:scale-105"
              >
                <span>📱</span>
                <span>تطبيق الأندرويد</span>
              </a>
              <a
                href="https://www.tad-ai.com/SARA-AI.apk"
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-2 rounded-xl py-2.5 px-4 sm:px-5 text-white text-xs sm:text-sm font-bold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-lg shadow-purple-500/20 transition-all hover:scale-105"
              >
                <span>🤖</span>
                <span>تطبيق سارة (APK)</span>
              </a>
            </div>

            {/* Compact Live Market Status Strip */}
            <div className="bg-[#0F172A]/85 backdrop-blur-xl rounded-2xl p-4 border border-white/10 shadow-xl relative overflow-hidden text-right">
              <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-3 mb-3">
                <div className="flex items-center gap-2.5">
                  {isMarketOpen ? (
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                    </span>
                  ) : (
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gray-500"></span>
                    </span>
                  )}
                  <span className="text-white font-bold text-xs sm:text-sm">
                    {isMarketOpen ? t('heroStatusLive') : (status === 'holiday' ? t('marketStatusHoliday') : t('heroStatusClosed'))}
                  </span>
                </div>
                <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${isMarketOpen ? 'text-green-400 bg-green-400/10' : 'text-gray-400 bg-gray-400/10'}`}>
                  {isMarketOpen ? '● جلسة جارية' : 'السوق مغلق'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                <div className="flex justify-between items-center bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/5">
                  <span className="text-gray-400">{t('heroStocksMonitored')}</span>
                  <span className="text-white font-bold font-mono">230+</span>
                </div>
                <div className="flex justify-between items-center bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/5">
                  <span className="text-gray-400">{isMarketOpen ? t('heroEntryPointsToday') : t('heroEntryPointsRecent')}</span>
                  <span className={`${isMarketOpen ? 'text-green-400' : 'text-gray-300'} font-bold font-mono text-[10px]`}>{isMarketOpen ? 'للمشتركين' : '—'}</span>
                </div>
              </div>

              <a
                href="https://t.me/Tad_cs_BOT"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full flex items-center justify-center gap-2 text-white py-2 rounded-xl text-xs font-bold transition-all shadow-md 
                  ${isMarketOpen
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400'
                    : 'bg-white/10 hover:bg-white/15 border border-white/10'}`}
              >
                💬 {isMarketOpen ? t('heroExpertAskNow') : t('heroExpertTalkNow')}
              </a>
            </div>

          </div>

          {/* Left Column: Interactive Video Player Above The Fold */}
          <div className="lg:col-span-7 xl:col-span-7 w-full flex items-center justify-center">
            <HeroVideoPlayer />
          </div>

        </div>
      </div>

      {/* Circular fading overlay */}
      <div className="hero-fade-circle"></div>

      {/* CSS additions for this specific component */}
      <style>{`
        html { scroll-behavior: smooth; }
      `}</style>
    </section>
  );
};

export default Hero;
